import { motion } from "framer-motion";
import { FiCheck, FiArrowRight, FiZap, FiStar, FiClock } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";

function getPlanIndex(planId) {
  return ({ free: 0, pro: 1, enterprise: 2 }[planId] || 0);
}

export default function PlanCard({ plan, currentPlan, onSelectPlan, loading, featured = false }) {
  const { t, isRTL } = useLanguage();
  const { darkMode }  = useDarkMode();

  const isCurrentPlan  = currentPlan?.id === plan.id;
  const isDowngrade    = getPlanIndex(currentPlan?.id) > getPlanIndex(plan.id);
  const isUpgrade      = getPlanIndex(currentPlan?.id) < getPlanIndex(plan.id);
  // All plans are currently in "coming soon" mode — plan switching is
  // disabled until billing is wired up. The cards still display
  // pricing / quotas / features so users can compare, but the action
  // button is locked and shows "غير متاح حالياً" / "Coming soon".
  // System admins can still change a tenant's plan from /admin (that
  // path uses updateTenantSettings, which doesn't go through this
  // marketing card).
  const isComingSoon   = true;

  /* ── Card border & background ──────────────────────────── */
  const cardBg = darkMode
    ? `bg-gray-800 ${featured ? "border-blue-500" : isCurrentPlan ? "border-green-500" : isComingSoon ? "border-gray-600" : "border-gray-700"}`
    : `bg-white  ${featured ? "border-blue-500" : isCurrentPlan ? "border-green-500" : isComingSoon ? "border-gray-300" : "border-gray-200"}`;

  /* ── Button styles ─────────────────────────────────────── */
  const btnCls = isComingSoon
    ? darkMode
      ? "bg-gray-700/50 text-gray-500 cursor-not-allowed"
      : "bg-gray-100 text-gray-400 cursor-not-allowed"
    : isCurrentPlan
      ? darkMode ? "bg-gray-700 text-gray-400 cursor-not-allowed" : "bg-gray-100 text-gray-400 cursor-not-allowed"
      : featured
        ? "bg-blue-600 hover:bg-blue-700 text-white"
        : isDowngrade
          ? darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-900"
          : darkMode ? "bg-gray-700 hover:bg-blue-600 text-white" : "bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-900";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={isComingSoon ? {} : { y: -5 }}
      transition={{ duration: 0.3 }}
      className={`relative border rounded-xl p-6 ${cardBg}
        ${featured ? "shadow-xl shadow-blue-500/20" : "shadow-lg"}
        ${isComingSoon ? "opacity-80" : ""}`}
    >
      {/* ── Featured badge ─────────────────────────────────── */}
      {featured && !isComingSoon && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-blue-600 text-white text-sm px-4 py-1 rounded-full flex items-center gap-1">
            <FiStar className="text-xs" /><span>{t("most_popular")}</span>
          </div>
        </div>
      )}

      {/* ── Current plan badge ─────────────────────────────── */}
      {isCurrentPlan && !isComingSoon && (
        <div className={`absolute -top-4 ${isRTL ? "left-4" : "right-4"}`}>
          <div className="bg-green-600 text-white text-sm px-4 py-1 rounded-full flex items-center gap-1">
            <FiCheck className="text-xs" /><span>{t("current_plan_badge")}</span>
          </div>
        </div>
      )}

      {/* ── Coming Soon badge ──────────────────────────────── */}
      {isComingSoon && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className={`text-sm px-4 py-1 rounded-full flex items-center gap-1.5
            ${darkMode ? "bg-gray-700 text-gray-300 border border-gray-600" : "bg-gray-200 text-gray-600 border border-gray-300"}`}>
            <FiClock className="text-xs" />
            <span>{isRTL ? "قريباً" : "Coming Soon"}</span>
          </div>
        </div>
      )}

      {/* ── Plan header ────────────────────────────────────── */}
      <div className="text-center mb-6">
        <h3 className={`text-2xl font-bold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
          {t(`plan_${plan.id}`) || plan.name}
        </h3>
        <p className={`text-sm mb-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
          {t(`${plan.id}_plan_desc`) || plan.description}
        </p>

        {/* Price — SAR instead of $ */}
        {isComingSoon ? (
          <div className="flex items-baseline justify-center gap-1">
            <span className={`text-lg font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              {isRTL ? "قريباً" : "Coming Soon"}
            </span>
          </div>
        ) : (
          <div className="flex items-baseline justify-center gap-1">
            <span className={`text-4xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
              {plan.price}
            </span>
            <span className={`text-lg font-medium ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
              {/* Currency switches with language: ر.س for Arabic, SAR
                  for English. Was hardcoded to ر.س even on the EN UI. */}
              {isRTL ? "ر.س" : "SAR"}
            </span>
            <span className={darkMode ? "text-gray-400" : "text-gray-500"}>
              /{t("month")}
            </span>
          </div>
        )}
      </div>

      {/* ── Features list ──────────────────────────────────── */}
      <div className="space-y-3 mb-6">
        {plan.highlights?.map((feature, i) => (
          <div key={i} className="flex items-start gap-2">
            <FiCheck className={`mt-1 flex-shrink-0 ${isComingSoon ? "text-gray-400" : "text-green-400"}`} />
            <span className={`text-sm ${
              isComingSoon
                ? darkMode ? "text-gray-500" : "text-gray-400"
                : darkMode ? "text-gray-300" : "text-gray-600"
            }`}>
              {t(feature)}
            </span>
          </div>
        ))}
      </div>

      {/* ── Action button ──────────────────────────────────── */}
      <button
        onClick={() => !isComingSoon && onSelectPlan(plan.id)}
        disabled={loading || isCurrentPlan || isComingSoon}
        className={`w-full py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${btnCls}`}
      >
        {loading && !isComingSoon ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            <span>{t("processing")}</span>
          </>
        ) : isComingSoon ? (
          <>
            <FiClock />
            <span>{isRTL ? "غير متاح حالياً" : "Not Available Yet"}</span>
          </>
        ) : isCurrentPlan ? (
          <><FiCheck /><span>{t("current_plan_badge")}</span></>
        ) : isUpgrade ? (
          <><FiZap /><span>{t("upgrade_to")} {plan.name}</span><FiArrowRight /></>
        ) : isDowngrade ? (
          <span>{t("downgrade_to")} {plan.name}</span>
        ) : (
          <><span>{t("select_plan_btn")} {plan.name}</span><FiArrowRight /></>
        )}
      </button>

      {/* ── Quotas ─────────────────────────────────────────── */}
      <div className={`mt-4 pt-4 border-t ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            { label: t("evaluations"),    val: plan.quotas?.evaluations_monthly },
            { label: t("total_campaigns"), val: plan.quotas?.campaigns_max },
            { label: t("share_links"),    val: plan.quotas?.share_links_max },
            { label: t("pdfs_generated"), val: plan.quotas?.pdf_monthly, zero: t("not_included") },
          ].map((q, i) => (
            <div key={i} className={isRTL ? "text-right" : ""}>
              <span className={`font-medium ${
                isComingSoon
                  ? darkMode ? "text-gray-600" : "text-gray-400"
                  : darkMode ? "text-gray-400" : "text-gray-500"
              }`}>
                {q.label}:{" "}
              </span>
              <span className={
                isComingSoon
                  ? darkMode ? "text-gray-600" : "text-gray-400"
                  : darkMode ? "text-gray-300" : "text-gray-700"
              }>
                {isComingSoon
                  ? "—"
                  : (q.val === 999999 || q.val === -1)
                    ? t("unlimited")
                    : q.val === 0 && q.zero
                      ? q.zero
                      : i === 0 || i === 3
                        ? `${q.val}/${isRTL ? "شهر" : "mo"}`
                        : q.val}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}